/**
 * HTTP client for live console APIs.
 *
 * - Hierarchy: Vite `/api/hierarchy` proxy (or `VITE_API_BASE_URL`)
 * - Analytics: Vite `/api/analytics` proxy (or `VITE_ANALYTICS_BASE_URL`)
 * - Wallet: Vite `/api/wallet` proxy (or `VITE_WALLET_BASE_URL`)
 * - Bet: Vite `/api/bet` proxy (or `VITE_BET_BASE_URL`)
 * - Socket: Vite `/api/socket` proxy (or `VITE_SOCKET_BASE_URL`) — live odds SSE
 *
 * Auth = Keycloak OIDC only (OpenAPI `bearerAuth`):
 * - `Authorization: Bearer` from the signed-in session
 * - Acting node = JWT `agentId` claim
 *
 * Demo session → hierarchy and analytics stay on fixtures.
 * Live Keycloak → hierarchy (+ analytics/wallet/bet only when env URLs are set).
 * Never silent fixture swap on a live session.
 */

import { getAccessToken, isDemoSession, decodeClaims } from '@/auth/oidc'

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export function getApiBaseUrl(): string | null {
  const raw = import.meta.env.VITE_API_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  if (import.meta.env.DEV) return '/api/hierarchy'
  return null
}

export function getAnalyticsBaseUrl(): string | null {
  const raw = import.meta.env.VITE_ANALYTICS_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  // No DEV default — analytics is contract-only; avoid local mock invented numbers.
  return null
}

export function getWalletBaseUrl(): string | null {
  const raw = import.meta.env.VITE_WALLET_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  // No DEV default — only call wallet when explicitly pointed at a real service.
  return null
}

export function getBetBaseUrl(): string | null {
  const raw = import.meta.env.VITE_BET_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  // No DEV default — only call bet-service when explicitly pointed at a real service.
  return null
}

/**
 * Agent-API workstation bet actions (invalid / revalidate).
 * Falls back to the shared bet-service base so existing setups keep working.
 */
export function getAgentBetBaseUrl(): string | null {
  const raw = import.meta.env.VITE_AGENT_BET_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  return getBetBaseUrl()
}

export function getPlayerBaseUrl(): string | null {
  const raw = import.meta.env.VITE_PLAYER_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  if (import.meta.env.DEV) return '/api/player'
  return null
}

/** Live Socket-API SSE hub (Frontend-UI SOCKET_API_URL). Same-origin `/api/socket` in DEV. */
export function getSocketBaseUrl(): string | null {
  const raw = import.meta.env.VITE_SOCKET_BASE_URL
  if (typeof raw === 'string') {
    const trimmed = raw.trim().replace(/\/$/, '')
    if (trimmed.length > 0) return trimmed
  }
  if (import.meta.env.DEV) return '/api/socket'
  return null
}

/** Live Player-API admin MFA reads (Keycloak session + player base URL). Demo → fixtures. */
export function hasLivePlayerMfa(): boolean {
  if (isDemoSession()) return false
  return Boolean(getPlayerBaseUrl() && getBearerToken())
}

/** Local-only agent uuid from `.env.local` — never used in production builds. */
function getDevCallerId(): string | null {
  if (!import.meta.env.DEV) return null
  const raw = import.meta.env.VITE_CALLER_ID
  if (typeof raw !== 'string') return null
  const id = raw.trim()
  return id.length > 0 ? id : null
}

/**
 * Acting hierarchy / analytics node for a Live (Keycloak) session.
 * Prefer JWT `agentId`; in DEV fall back to `VITE_CALLER_ID` when the token
 * lacks the claim. Demo sessions never get a caller id — they stay on fixtures.
 */
export function getCallerAgentId(): string | null {
  if (isDemoSession()) return null
  const claims = decodeClaims(getAccessToken())
  const id = claims?.agentId?.trim()
  if (id && id.length > 0) return id
  // Aggregate groups: JWT may carry aggregateGroupId instead of agentId.
  const aggregateId = claims?.aggregateGroupId?.trim()
  if (aggregateId && aggregateId.length > 0) return aggregateId
  return getDevCallerId()
}

function getBearerToken(): string | null {
  if (isDemoSession()) return null
  const session = getAccessToken()
  return session?.trim() ? session.trim() : null
}

export { getBearerToken }

/**
 * Live hierarchy when signed in with Keycloak + a base URL. Demo → fixtures.
 *
 * Agent desks need JWT `agentId` (or DEV `VITE_CALLER_ID`). Aggregate Keycloak
 * often has only realm role `aggregate` — group id is attached on `/v1/me`, not
 * always on the access token — so Aggregate sessions are live with Bearer alone.
 */
export function hasLiveApi(): boolean {
  if (isDemoSession()) return false
  if (!getApiBaseUrl() || !getBearerToken()) return false
  if (getCallerAgentId()) return true
  const claims = decodeClaims(getAccessToken())
  if (!claims) return false
  const roles = (claims.roles ?? []).map((r) => String(r).toLowerCase())
  return roles.includes('aggregate') || claims.accessLevel === 'Aggregate'
}

/**
 * Live analytics for Keycloak sessions only. Demo login always uses fixture
 * report data — never the analytics mock / service.
 */
export function hasLiveAnalytics(): boolean {
  if (isDemoSession()) return false
  return Boolean(getAnalyticsBaseUrl() && getBearerToken() && getCallerAgentId())
}

export function hasLiveWallet(): boolean {
  if (isDemoSession()) return false
  return Boolean(getWalletBaseUrl() && getBearerToken() && getCallerAgentId())
}

export function hasLiveBet(): boolean {
  if (isDemoSession()) return false
  if (!getBetBaseUrl() || !getBearerToken()) return false
  if (getCallerAgentId()) return true
  // Aggregate JWT often has realm role only — books use /v1/me memberList roots.
  const claims = decodeClaims(getAccessToken())
  if (!claims) return false
  const roles = (claims.roles ?? []).map((r) => String(r).toLowerCase())
  return roles.includes('aggregate') || claims.accessLevel === 'Aggregate'
}

export function hasLiveAgentBet(): boolean {
  if (isDemoSession()) return false
  return Boolean(getAgentBetBaseUrl() && getBearerToken() && getCallerAgentId())
}

/** Live odds stream — Keycloak session + Socket-API base (public SSE, no key). Demo stays on fixture ladders. */
export function hasLiveSocket(): boolean {
  if (isDemoSession()) return false
  return Boolean(getSocketBaseUrl() && getBearerToken() && getCallerAgentId())
}

function authHeaders(headers: Headers): void {
  const bearer = getBearerToken()
  if (bearer) headers.set('Authorization', `Bearer ${bearer}`)
  // analytics-service interim identity (OpenAPI callerId) — same uuid as path {id}.
  const caller = getCallerAgentId()
  if (caller) headers.set('X-Caller-Id', caller)
}

async function fetchJson<T>(
  base: string,
  path: string,
  init: RequestInit,
  unreachableMsg: string,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`
  const headers = new Headers(init.headers)
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  authHeaders(headers)

  let response: Response
  try {
    response = await fetch(url, { ...init, headers })
  } catch {
    throw new ApiError(0, unreachableMsg, null)
  }
  const text = await response.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }

  if (!response.ok) {
    const message =
      typeof body === 'object' &&
      body &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : `Request failed (${response.status})`
    throw new ApiError(response.status, message, body)
  }

  return body as T
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getApiBaseUrl()
  if (!base) throw new Error('VITE_API_BASE_URL is not set')
  return fetchJson(
    base,
    path,
    init,
    'Hierarchy API unreachable — start hierarchy on :3910 or port-forward the cluster service',
  )
}

export async function analyticsFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getAnalyticsBaseUrl()
  if (!base) throw new Error('VITE_ANALYTICS_BASE_URL is not set')
  return fetchJson(
    base,
    path,
    init,
    'Analytics API unreachable — start analytics-service or set VITE_ANALYTICS_BASE_URL',
  )
}

export async function walletFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getWalletBaseUrl()
  if (!base) throw new Error('VITE_WALLET_BASE_URL is not set')
  return fetchJson(base, path, init, 'Wallet API unreachable — start wallet mock on :3900')
}

export async function betFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getBetBaseUrl()
  if (!base) throw new Error('VITE_BET_BASE_URL is not set')
  return fetchJson(base, path, init, 'Bet API unreachable — start bet mock on :3930')
}

export async function agentBetFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getAgentBetBaseUrl()
  if (!base) throw new Error('VITE_AGENT_BET_BASE_URL or VITE_BET_BASE_URL is not set')
  return fetchJson(
    base,
    path,
    init,
    'Agent-API bet endpoint unreachable — start Agent-API bet-service on :3930',
  )
}

export async function playerFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = getPlayerBaseUrl()
  if (!base) throw new Error('VITE_PLAYER_BASE_URL is not set')
  return fetchJson(
    base,
    path,
    init,
    'Player API unreachable — start Player-API on :3040 and set PLAYER_ADMIN_API_KEY for the Vite proxy',
  )
}
